/* Dependency Imports */
import React, { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Box,
  Link,
  TextField,
  Button,
  InputAdornment,
  InputLabel,
  FormControl,
  OutlinedInput,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GroupsIcon from "@mui/icons-material/Groups";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { Navigate } from "react-router-dom";

/* Project Imports */
import ForgotPassword from "./ForgotPassword";
import LoadingLogo from "../common/LoadingLogo";
import { useUserQuery, useLogin } from "../../features/auth/authHooks";
import logo from "../../assets/sv_logo_green.png";

const FEATURES = [
  {
    icon: <GroupsIcon sx={{ fontSize: 28 }} />,
    title: "Registrant & contact management",
    description: "Every lead is captured, assigned, and tracked from first contact through to close.",
  },
  {
    icon: <ForwardToInboxIcon sx={{ fontSize: 28 }} />,
    title: "Appointments & outreach",
    description: "SMS, calls, email blasts, walk-ins and appointments, all logged against the contact.",
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 28 }} />,
    title: "Deals, tasks & reporting",
    description: "Price grids, deal writing, and task tracking keep every file moving forward.",
  },
];

const Login = () => {
  /* Redux/Hooks */
  const theme = useTheme();
  const { loading, data } = useUserQuery(false);
  const client = useApolloClient();
  const login = useLogin();

  /* States */
  const [email, setEmail] = useState("");
  const [password, setpassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  /* Functions */

  useEffect(() => {
    client.resetStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    login(email, password);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setpassword(event.target.value);
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleShowForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const scrollToFeatures = (event: React.MouseEvent) => {
    event.preventDefault();
    document.getElementById("login-features")?.scrollIntoView({ behavior: "smooth" });
  };

  return loading ? (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: "50%",
        "-webkit-transform": "translate(-50%, -50%)",
        transform: "translate(-50%, -50%)",
      }}
    >
      <LoadingLogo />
    </Box>
  ) : data?.getUser ? (
    <>
      <Navigate to="/dashboard" />
    </>
  ) : (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#faf8f4",
        "@media (max-width: 1300px)": { zoom: 1.25 },
        "@media (min-height: 1200px)": { zoom: 1.3 },
        "@media (min-height: 1800px)": { zoom: 1.6 },
        "@media (min-height: 2400px)": { zoom: 2 },
      }}
    >
      {/* Top Nav */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3, md: 6 },
          py: 2,
          borderBottom: "1px solid #e8e3da",
        }}
      >
        <img src={logo} alt="logo" width="120px" style={{ maxWidth: "35vw", height: "auto" }} />
      </Box>

      {/* Hero */}
      <Box
        sx={{
          flex: "1 0 auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: { xs: "center", md: "space-between" },
          gap: { xs: 5, md: 8 },
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 5, md: 10 },
          width: "100%",
          maxWidth: "1200px",
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ maxWidth: "560px", width: "100%" }}>
          <Typography
            sx={{
              color: "#8a7d6b",
              letterSpacing: "2px",
              fontSize: { xs: 11, sm: 13 },
              fontWeight: 600,
              mb: 2,
            }}
          >
            PROPRIETARY CRM PLATFORM
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              fontSize: { xs: "1.85rem", sm: "2.25rem", md: "3rem" },
              lineHeight: 1.15,
              color: "#1a1a1a",
              mb: 3,
            }}
          >
            Industry-leading Sales Engine.
          </Typography>
          <Typography sx={{ color: "#5a5548", fontSize: { xs: 15, sm: 17 }, lineHeight: 1.6 }}>
            Registrant and contact management, SMS &amp; call tracking, appointments, and deal
            writing &mdash; in one workflow built in-house at RDS for real estate sales teams.
          </Typography>
        </Box>

        {/* Login Card */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e8e3da",
            borderRadius: "4px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            p: { xs: 3, sm: 4 },
            width: "100%",
            maxWidth: "400px",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "#1a1a1a", mb: 3 }}>
            Sign in to your CRM
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              autoFocus
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
              <InputLabel>Password *</InputLabel>
              <OutlinedInput
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={handePasswordChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={handleShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ backgroundColor: theme.palette.primary.main, py: 1.25, "&:hover": { backgroundColor: "#233731" } }}
            >
              Log In
            </Button>
          </Box>
          <Link
            sx={{ textDecoration: "none", cursor: "pointer", display: "block", mt: 2, fontStyle: "italic", color: "#5a5548" }}
            variant="body2"
            onClick={handleShowForgotPassword}
          >
            Forgot your password?
          </Link>
        </Box>
      </Box>

      {/* Feature Strip */}
      <Box
        id="login-features"
        sx={{ flexShrink: 0, backgroundColor: "#f1ece2", py: { xs: 5, md: 7 }, px: { xs: 2, sm: 3, md: 6 } }}
      >
        <Typography
          align="center"
          sx={{ color: "#8a7d6b", letterSpacing: "2px", fontSize: { xs: 11, sm: 13 }, fontWeight: 600, mb: 5 }}
        >
          ONE PLATFORM, END TO END
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 4, md: 5 },
            maxWidth: "1100px",
            mx: "auto",
          }}
        >
          {FEATURES.map((feature) => (
            <Box key={feature.title} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Box sx={{ color: theme.palette.primary.main, mb: 1.5 }}>{feature.icon}</Box>
              <Typography sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.75 }}>{feature.title}</Typography>
              <Typography sx={{ color: "#5a5548", fontSize: 14, lineHeight: 1.6 }}>{feature.description}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <ForgotPassword open={showForgotPassword} setOpen={setShowForgotPassword} />
    </Box>
  );
};

export default Login;
